import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../useStore";
import { crearHistorialPedido } from "../../repositorios/apiServer";

import LeftPanel from "./LeftPanel";
import CarritoPanel from "./CarritoPanel";
import OnboardingScreen from "./OnboardingScreen";
import BotonInstalarPWA from "./BotonInstalarPWA";

import { useCarrito } from "./hooks/useCarrito";
import { useCategorias } from "./hooks/useCategorias";
import { useScrollSync } from "./hooks/useScrollSync";

import style from "./Carta.module.css";

export default function Carta() {
  const navigate = useNavigate();

  const [loadingComplete, setLoadingComplete] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [miRomanaVisible, setMiRomanaVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrollingToSection, setScrollingToSection] = useState(false);
  const [showAddedMessage, setShowAddedMessage] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  const menuRef = useRef(null);
  const contentRef = useRef(null);
  const itemsRef = useRef(null);
  const headerPartRef = useRef(null);
  const miRomanaRef = useRef(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftPosition, setScrollLeftPosition] = useState(0);

  const categoriesData = useCategorias();
  const carritoData = useCarrito(isMobile);

  const {
    categorias,
    categoriaID,
    setCategoriaID,
    mostrarVinos,
    cargando,
    categoriasSinProductos,
    preloadedProducts,
    sectionRefs,
    getID,
    registrarCategoriaSinProductos,
    toggleVinos,
  } = categoriesData;

  const {
    productosSeleccionados,
    carritoVisible,
    toggleCarrito,
    agregarProducto: agregarProductoOriginal,
    eliminarProducto,
    actualizarCantidad,
    limpiarCarrito,
  } = carritoData;

  const agregarProducto = (producto) => {
    agregarProductoOriginal(producto);
    setFadeOut(false);
    setShowAddedMessage(true);

    setTimeout(() => {
      setFadeOut(true);
    }, 1500);

    setTimeout(() => {
      setShowAddedMessage(false);
    }, 2500);
  };

  useScrollSync({
    categorias,
    categoriaID,
    setCategoriaID,
    scrollingToSection,
    categoriasSinProductos,
    itemsRef,
    getID,
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1022);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (miRomanaRef.current && !miRomanaRef.current.contains(event.target)) {
        setMiRomanaVisible(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!menuRef.current || !headerPartRef.current || !itemsRef.current)
        return;

      if (contentRef.current) {
        contentRef.current.style.paddingTop = `${
          menuRef.current.offsetHeight - 200
        }px`;
      }
    };

    if (contentRef.current && menuRef.current) {
      contentRef.current.style.paddingTop = `${
        menuRef.current.offsetHeight - 200
      }px`;
    }

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (itemsRef.current) {
      const slider = itemsRef.current;
      slider.style.display = "flex";
      slider.style.overflowX = "auto";
      slider.style.scrollbarWidth = "none";
      slider.style.msOverflowStyle = "none";
      slider.style.cursor = isMouseDown ? "grabbing" : "grab";
      slider.style.userSelect = "none";
      slider.style.cssText += "::-webkit-scrollbar { display: none; }";
    }
  }, [isMouseDown]);

  useEffect(() => {
    if (!cargando) {
      setLoadingComplete(true);
      const onboardingCompleted = localStorage.getItem("onboardingCompleted");
      if (onboardingCompleted !== "true") {
        setShowOnboarding(true);
      }
    }
  }, [cargando]);

  const centerTab = useCallback((index, immediate = false) => {
    if (!itemsRef.current) return;

    const botones = itemsRef.current.querySelectorAll("button");
    if (!botones || index >= botones.length) return;

    const tab = botones[index];
    const container = itemsRef.current;

    if (container && tab) {
      const offset =
        tab.getBoundingClientRect().left -
        container.getBoundingClientRect().left;
      const scrollTo = offset - container.offsetWidth / 2 + tab.offsetWidth / 2;

      container.scrollTo({
        left: scrollTo,
        behavior: immediate ? "auto" : "smooth",
      });
    }
  }, []);

  const encontrarIndiceCategoriaVisible = useCallback(
    (categoriaId) => {
      const categoriasVisibles = categorias.filter(
        (cat) => !categoriasSinProductos.has(getID(cat))
      );
      const indice = categoriasVisibles.findIndex(
        (cat) => getID(cat) === categoriaId
      );
      return indice;
    },
    [categorias, categoriasSinProductos, getID]
  );

  useEffect(() => {
    if (categoriaID && itemsRef.current) {
      const indice = encontrarIndiceCategoriaVisible(categoriaID);
      if (indice !== -1) {
        setTimeout(() => {
          centerTab(indice);
        }, 50);
      }
    }
  }, [categoriaID, centerTab, encontrarIndiceCategoriaVisible]);

  const handleMouseDown = (e) => {
    setIsMouseDown(true);
    setStartX(e.pageX - itemsRef.current.offsetLeft);
    setScrollLeftPosition(itemsRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsMouseDown(false);
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
  };

  const handleMouseMove = (e) => {
    if (!isMouseDown) return;
    e.preventDefault();
    const x = e.pageX - itemsRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    itemsRef.current.scrollLeft = scrollLeftPosition - walk;
  };

  const handleCategoriaClick = (categoria) => {
    const categoriaId = categoria._id || categoria.id;
    setCategoriaID(categoriaId);

    const indice = encontrarIndiceCategoriaVisible(categoriaId);
    if (indice !== -1) {
      centerTab(indice, false);
    }

    const sectionRef = sectionRefs.current[categoriaId];
    if (sectionRef && sectionRef.current) {
      setScrollingToSection(true);

      sectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      sectionRef.current.classList.add(style.highlight_section);
      setTimeout(() => {
        sectionRef.current.classList.remove(style.highlight_section);
      }, 1000);

      setTimeout(() => {
        setScrollingToSection(false);
      }, 1000);
    }
  };

  const irPerfil = () => {
    if (useStore.getState().userToken) {
      navigate("/editar_perfil");
    } else {
      navigate("/login");
    }
  };

  const toggleReservas = () => {
    navigate("/reservas");
  };

  const boton_recomendaciones = () => {
    if (useStore.getState().userToken) {
      navigate("/recomendaciones");
    } else {
      navigate("/login");
    }
  };

  const toggleMiRomana = () => {
    setMiRomanaVisible(!miRomanaVisible);
  };

  const handleMiRomanaOption = (option) => {
    if (useStore.getState().userToken) {
      if (option === "favoritos") {
        navigate("/favoritos");
      } else if (option === "historial") {
        navigate("/historialpedidos");
      }
    } else {
      navigate("/login");
    }
    setMiRomanaVisible(false);
  };

  const realizarPedido = async () => {
    if (productosSeleccionados.length === 0) {
      alert("No hay productos seleccionados para realizar el pedido");
      return;
    }

    if (!useStore.getState().userToken) {
      navigate("/login");
      return;
    }

    try {
      const userId = useStore.getState().userToken;

      const productosParaGuardar = productosSeleccionados.map((producto) => {
        const productoId = producto._id || producto.id;

        return {
          _id: productoId,
          nombre: producto.nombre,
          precio: producto.precio,
          cantidad: producto.cantidad || 1,
          esVino: producto.esVino === true,
        };
      });

      await crearHistorialPedido(userId, productosParaGuardar);
      limpiarCarrito();
    } catch (error) {
      alert("Ha ocurrido un error al realizar el pedido. Inténtalo de nuevo.");
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    localStorage.setItem("onboardingCompleted", "true");
  };

  return (
    <div className={style.main_wrapper}>
      <OnboardingScreen
        isVisible={showOnboarding}
        onComplete={handleOnboardingComplete}
      />
      \
      {showAddedMessage && isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: "90px",
            left: "50%",
            transform: `translateX(-50%) translateY(${fadeOut ? 0 : -10}px)`,
            backgroundColor: "rgb(243, 221, 169)",
            color: "black",
            padding: "10px 20px",
            borderRadius: "5px",
            fontWeight: "bold",
            zIndex: 9999,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            textAlign: "center",
            width: "auto",
            minWidth: "150px",
            border: "1px solid black",
            opacity: fadeOut ? 0 : 1,
            transition:
              "opacity 1s ease, transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
          }}
        >
          ¡Añadido!
        </div>
      )}
      <LeftPanel
        onPerfilClick={irPerfil}
        onVinosClick={toggleVinos}
        mostrarVinos={mostrarVinos}
        headerPartRef={headerPartRef}
        menuRef={menuRef}
        categorias={categorias}
        categoriasSinProductos={categoriasSinProductos}
        categoriaID={categoriaID}
        getID={getID}
        onCategoriaClick={handleCategoriaClick}
        itemsRef={itemsRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        sectionRefs={sectionRefs}
        preloadedProducts={preloadedProducts}
        onProductoClick={agregarProducto}
        registrarCategoriaSinProductos={registrarCategoriaSinProductos}
        cargando={cargando}
        contentRef={contentRef}
        onRecomendaciones={boton_recomendaciones}
        miRomanaVisible={miRomanaVisible}
        onToggleMiRomana={toggleMiRomana}
        onMiRomanaOption={handleMiRomanaOption}
        miRomanaRef={miRomanaRef}
        onReservasClick={toggleReservas}
        isMobile={isMobile}
      />
      <CarritoPanel
        productosSeleccionados={productosSeleccionados}
        onEliminarProducto={eliminarProducto}
        onActualizarCantidad={actualizarCantidad}
        onRealizarPedido={realizarPedido}
        onToggleCarrito={toggleCarrito}
        carritoVisible={carritoVisible}
        isMobile={isMobile}
      />
      <button
        className={`${style.boton_carrito_flotante} ${
          showOnboarding ? style.hidden : ""
        }`}
        onClick={toggleCarrito}
      >
        📋
        {productosSeleccionados.length > 0 && (
          <span className={style.carrito_badge}>
            {productosSeleccionados.reduce(
              (total, producto) => total + (producto.cantidad || 1),
              0
            )}
          </span>
        )}
      </button>
      <div
        className={`${style.overlay} ${carritoVisible ? style.visible : ""}`}
        onClick={toggleCarrito}
      ></div>
    </div>
  );
}
